/**
 * Rotulos e avisos do segurado (P-SEG-1), num lugar so, para que a lista, o detalhe e
 * o formulario da operacao digam a mesma coisa. Ver docs/ADR/0029-segurado.md.
 */

import type {
  Insured,
  InsuredPartnerRole,
  InsuredSector,
} from '@/shared/api/resources';

export const PERMISSAO_VER = 'segurados.segurado.view';
export const PERMISSAO_CRIAR = 'segurados.segurado.create';
export const PERMISSAO_EDITAR = 'segurados.segurado.update';

export const ramos: { value: InsuredSector; label: string }[] = [
  { value: 'publico', label: 'Publico' },
  { value: 'privado', label: 'Privado' },
];

export function rotuloDoRamo(ramo: string): string {
  return ramos.find((item) => item.value === ramo)?.label ?? ramo;
}

export function rotuloDoTipo(tipo: string): string {
  return tipo === 'pf' ? 'Pessoa fisica' : tipo === 'pj' ? 'Pessoa juridica' : tipo;
}

export function rotuloDoPep(situacao: Insured['pep_status']): string {
  switch (situacao) {
    case 'aprovado':
      return 'Aprovado';
    case 'recusado':
      return 'Recusado';
    default:
      return 'Nao verificado';
  }
}

/** Nome das acoes da trilha do segurado, para o Historico de Alteracoes. */
const acoes: Record<string, string> = {
  'segurados.segurado.create': 'Cadastro do segurado',
  'segurados.segurado.update': 'Alteracao do cadastro',
  'segurados.segurado.block': 'Bloqueio',
  'segurados.segurado.unblock': 'Desbloqueio',
  'segurados.endereco.create': 'Endereco incluido',
  'segurados.endereco.update': 'Endereco alterado',
  'segurados.endereco.delete': 'Endereco excluido',
  'segurados.telefone.create': 'Telefone incluido',
  'segurados.telefone.update': 'Telefone alterado',
  'segurados.telefone.delete': 'Telefone excluido',
  'segurados.cnae.create': 'CNAE incluido',
  'segurados.cnae.update': 'CNAE alterado',
  'segurados.cnae.delete': 'CNAE excluido',
  'segurados.nome_fantasia.create': 'Nome fantasia incluido',
  'segurados.nome_fantasia.update': 'Nome fantasia alterado',
  'segurados.nome_fantasia.delete': 'Nome fantasia excluido',
  // Quadro societario (P-SEG-2).
  'segurados.socio.create': 'Pessoa incluida no quadro',
  'segurados.socio.update': 'Pessoa do quadro alterada',
  'segurados.socio.delete': 'Pessoa excluida do quadro',
};

export function rotuloDaAcao(acao: string): string {
  return acoes[acao] ?? acao;
}

/**
 * O efeito do bloqueio nao tem fonte (SEG-006): o bloqueio grava e mostra, e a
 * operacao continua aceitando o segurado.
 */
export const AVISO_BLOQUEIO =
  'O bloqueio fica registrado e aparece na lista e na operacao. O efeito dele na cotacao e na emissao ainda nao foi definido pela seguradora, e por isso ele nao impede a operacao.';

export const PENDENCIA_PEP =
  'Preenchido pela esteira PEP. A regra de verificacao do PEP do segurado esta pendente.';

// --- Abas do P-SEG-2 -----------------------------------------------------------

/** Os tres blocos da aba Socios / Administradores, na ordem dela. */
export const papeisDoQuadro: { value: InsuredPartnerRole; label: string }[] = [
  { value: 'socio', label: 'Socio' },
  { value: 'administrador', label: 'Administrador' },
  { value: 'representante', label: 'Representante' },
];

export function rotuloDoPapel(papel: string): string {
  return papeisDoQuadro.find((item) => item.value === papel)?.label ?? papel;
}

const origens: Record<string, string> = {
  consulta: 'Consulta',
  digitado: 'Digitado',
  importado: 'Importado',
};

export function rotuloDaOrigem(origem: string): string {
  return origens[origem] ?? origem;
}

/** Nome dos campos da trilha do segurado, para o "Visualizar alteracoes". */
const campos: Record<string, string> = {
  legal_name: 'Razao social',
  document: 'CPF/CNPJ',
  person_kind: 'Tipo de pessoa',
  sector: 'Ramo',
  email: 'E-mail',
  parent_insured_id: 'Matriz',
  commercial_registry: 'Junta comercial',
  status: 'Situacao',
  pep_status: 'PEP',
  block_reason: 'Motivo do bloqueio',
  zip_code: 'CEP',
  street: 'Logradouro',
  number: 'Numero',
  complement: 'Complemento',
  district: 'Bairro',
  city: 'Cidade',
  state: 'UF',
  city_code: 'Codigo IBGE do municipio',
  international: 'Exterior',
  position: 'Ordem',
  is_main: 'Principal',
  active: 'Ativo',
  phone: 'Telefone',
  cnae_code: 'CNAE',
  is_primary: 'Principal',
  name: 'Nome',
  parent_id: 'Socio de',
  role: 'Papel',
  role_description: 'Descricao do papel',
  document_kind: 'Tipo de documento',
  participation_bps: 'Participacao',
  is_pep: 'PEP',
  is_majority: 'Socio majoritario',
  document_unchecked: 'Documento nao conferido',
  source: 'Origem',
  removed_below: 'Pessoas excluidas junto',
};

export function rotuloDoCampo(campo: string): string {
  return campos[campo] ?? campo;
}

const itens: Record<string, string> = {
  segurado: 'Cadastro',
  endereco: 'Endereco',
  telefone: 'Telefone',
  cnae: 'CNAE',
  nome_fantasia: 'Nome fantasia',
  socio: 'Quadro societario',
};

export function rotuloDoItem(item: string): string {
  return itens[item] ?? item;
}

/** O PEP do socio so registra: a regra que o confere esta pendente (SEG-001). */
export const PENDENCIA_PEP_SOCIO =
  'O PEP e marcado por quem cadastra. A regra de verificacao do PEP do socio ainda nao foi definida pela seguradora, e nada e bloqueado.';

/** A consulta do quadro no bureau depende de decisao (D-01). */
export const PENDENCIA_QUADRO =
  'Sem a consulta do quadro societario no bureau, que ainda depende de decisao, as pessoas sao registradas a mao.';

/** Participacao que nao cabe em ponto-base (TEC-N02): recusada, sem arredondar. */
export const PENDENCIA_PARTICIPACAO =
  'Use ate duas casas decimais, como 33,33. Participacao com mais casas nao e arredondada: a forma de guardar o valor exato ainda esta pendente.';

/** Percentual de vigencia sem fonte (SEG-037). */
export const PENDENCIA_VIGENCIA =
  'A regra do percentual de vigencia ainda nao foi definida pela seguradora. A coluna fica vazia ate la.';

/** O cancelamento do SeguraCore e um pedido com aprovacao; a alcada nao tem fonte. */
export const AVISO_CANCELAMENTO =
  'No SeguraCore o cancelamento e um pedido: a apolice continua vigente ate a aprovacao. A alcada do cancelamento manual ainda nao foi definida pela seguradora.';
